/**
 * stack のフレーム整形（`safe-error-kind.ts` から分けた・2026-09-27 max-lines）。
 * 入口は従来どおり `safe-error-kind.ts` からも import できる（再輸出・実装はここだけ）。
 */

/**
 * stack から**フレーム行だけ**を返す（2026-09-13・Codex HIGH の実測から）。
 *
 * JS の `error.stack` は**先頭行が `Name: message`** で始まる。
 * つまり「message は出さない、stack は残す」と決めても、stack をそのまま出せば
 * message は出てしまう。実測で確認した:
 *
 *   PostgresError: duplicate key ... Key (phone)=(090-1234-5678) already exists.
 *       at Object.<anonymous> (/app/lib/db.ts:12:9)
 *       ...
 *
 * 🔴 2026-09-13 Codex HIGH: 初版は `/^\s+at\s/` に一致する行を**無条件で**残していた。
 * だが stack はただの文字列で、書き換えられる。`    at 090-1234-5678` のような値は通ってしまう。
 * **「フレームには値が入らない」という前提を、安全境界として使ってはいけない。**
 *
 * 形を厳格にする。残すのは `場所` が **`…:行:桁`** の形をしているフレームだけ:
 *   at 関数名 (/app/lib/db.ts:12:9)
 *   at /app/x.ts:3:1
 * 形が違えばフレームとみなさず落とす（取りこぼす側に倒す）。
 *
 * @returns `at <場所>` の行だけの文字列。Error でない・stack が無い・フレームが1行も無い場合は undefined
 */
/*
  🔴 2026-09-15 Codex HIGH: 関数名の位置と場所の位置の**両方**を形で縛る（関数名は出力しない）。
  🔴 ブラウザから送られた stack はここへ通さない（error-log route は null で保存・2026-09-27 実測）。
*/

/** 関数名の位置に許す形（V8 が出すものだけ）。空白は `async `/`new `/` [as x]` だけ・`-` は許さない。 */
const FRAME_FN = String.raw`(?:async |new )?[A-Za-z0-9_$.<>]+(?: \[as [A-Za-z0-9_$.<>]+\])?`

// 場所は `…:行:桁` で区切り（/ か \）を必ず含む。括弧は Next 16 の層名（WEBPACK_LAYERS_NAMES）だけ
export const NEXT_LAYER_NAMES: readonly string[] = [
  ...['shared', 'rsc', 'ssr', 'action-browser', 'api-node', 'api-edge', 'middleware', 'instrument'],
  ...['edge-asset', 'app-pages-browser', 'pages-dir-browser', 'pages-dir-edge', 'pages-dir-node'],
]
const LOC_CHAR = String.raw`(?:[^()\s]|\((?:${NEXT_LAYER_NAMES.join('|')})\))`
const FRAME_LOCATION = String.raw`${LOC_CHAR}*[/\\]${LOC_CHAR}*:\d+:\d+`

/** `at <関数名> (<場所>)` または `at <場所>` の、両方が形どおりのフレーム行だけを通す。 */
const FRAME_RE = new RegExp(
  String.raw`^\s+at\s+(?:${FRAME_FN}\s*\(\s*(?<loc>${FRAME_LOCATION})\s*\)|(?<bare>${FRAME_LOCATION}))\s*$`
)

/*
  🔴 2026-09-27 ROUTE-ERROR-STACK-PII-IN-PATH-1（Codex HIGH 3回）: 形で弾く方式は
     `/app/090-1234-5678/…`・関数名・`\` 区切り・英数字の前置・行:桁への分割で順に迂回された。
     関数名は出さない。**パスも行:桁も数字は1桁残らず `#` に伏せる**（stack は書き換えられ由来を
     検証できない・Codex HIGH 4・5回目）。message の先頭は切り取ってから読む（多重の防御）。
     英字の氏名だけはパスと区別できない。呼び出し元はサーバーの Error だけで、利用者の値はパスに入らない。
*/
// 1 = パス・2 = 行:桁（名前付きグループは型検査の target ES2017 で使えない・Issue #10723）
const LOCATION_RE = /^([A-Za-z0-9_.\-/\\:[\]@~+()]+)(:[1-9]\d{0,6}:[1-9]\d{0,6})$/
// メール（`@` の後が版番号 `#.#.#` でないもの）。数字を伏せた後のパスに当てる
const EMAIL_LIKE = /[^/\\]@(?!#[#.]*(?:[_/\\+-]|$))/

/** 形どおりのフレームを `at <数字を伏せた場所>` に作り直す。通さないなら undefined。 */
function toSafeFrame(line: string): string | undefined {
  const m = FRAME_RE.exec(line)
  const location = m?.groups?.loc ?? m?.groups?.bare
  const lm = location === undefined ? null : LOCATION_RE.exec(location)
  const rawPath = lm?.[1]
  const pos = lm?.[2]
  if (rawPath === undefined || pos === undefined) return undefined
  const path = rawPath.replace(/\d/g, '#')
  if (EMAIL_LIKE.test(path)) return undefined
  // 🔴 行:桁も必ず伏せる（Codex HIGH 5回目: stack は後から書き換えられ、由来を検証できない）
  return `    at ${path}${pos.replace(/\d/g, '#')}`
}

function framesOf(stack: string): string | undefined {
  const frames = stack.split('\n').flatMap((line) => toSafeFrame(line) ?? [])
  return frames.length > 0 ? frames.join('\n') : undefined
}

export function safeErrorFrames(value: unknown): string | undefined {
  let stack: unknown, head: unknown
  // 🔴 `instanceof` も try の中（safeErrorKind と同じ理由・2026-09-18）。
  try {
    if (!(value instanceof Error)) return undefined
    stack = value.stack
    head = `${value.name}: ${value.message}`
  } catch (_unreadableStack) {
    return undefined
  }
  if (typeof stack !== 'string' || typeof head !== 'string') return undefined
  // 🔴 message の改行でフレームを仕込める。先頭の `name: message` を切り取ってから読む。
  //    切り取れないのに改行を含むなら、どこまでが message か分からないので1行も返さない。
  if (stack.startsWith(head)) return framesOf(stack.slice(head.length))
  return head.includes('\n') ? undefined : framesOf(stack)
}

/**
 * `safeErrorFrames` の文字列版（台帳 CUSTOMER-PORTAL-ERROR-LOG-PII-1・2026-09-15）。
 *
 * ブラウザから送られてくる stack は **Error オブジェクトではなくただの文字列**なので、
 * `safeErrorFrames` を通せない。判定は1か所（`FRAME_RE`）に集約したまま入口だけ増やす。
 * 🔴 ここに別の正規表現を書かないこと。片方だけ緩むと、緩い側の入口から入ってくる。
 */
export function safeStackFrames(stack: string): string | undefined {
  return framesOf(stack)
}
